import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import { Add, Edit, Search } from "@mui/icons-material";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import PageContainer from "../../../components/container/PageContainer";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import {
  AddOrEditMachine,
  GetMachineDepartments,
  GetMachineTypes,
} from "../../../services/asaiadmin";

const BCrumb = [
  {
    to: "/",
    title: "ASAI Admin",
  },
  {
    title: "Machine Master",
  },
];

const emptyForm = {
  id: 0,
  departmentId: "",
  machineCode: "",
  machineType: "",
  description: "",
  isActive: true,
};

/* =========================================================
   RESPONSE HELPERS
   Support camelCase and PascalCase API responses.
   ========================================================= */

const getResponseRows = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  return [];
};

const normalizeMachine = (item) => {
  const activeValue = item.isActive ?? item.IsActive ?? true;

  return {
    id: item.id ?? item.Id ?? 0,
    departmentId: item.departmentId ?? item.DepartmentId ?? "",
    departmentName: item.departmentName ?? item.DepartmentName ?? "",
    machineCode: item.machineCode ?? item.MachineCode ?? "",
    machineName: item.machineName ?? item.MachineName ?? "",
    description: item.description ?? item.Description ?? "",
    isActive:
      activeValue === true ||
      activeValue === 1 ||
      activeValue === "1" ||
      activeValue === "true",
    createdBy: item.createdBy ?? item.CreatedBy ?? null,
    createdDate: item.createdDate ?? item.CreatedDate ?? null,
    modifiedBy: item.modifiedBy ?? item.ModifiedBy ?? null,
    modifiedDate: item.modifiedDate ?? item.ModifiedDate ?? null,
  };
};

const getResult = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data[0] ?? {};
  }

  return data ?? {};
};

const getResultMessage = (result) => result?.message ?? result?.Message ?? "";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ??
  error?.response?.data?.Message ??
  error?.message ??
  fallback;

/* =========================================================
   MACHINE MASTER
   ========================================================= */

const MachineMaster = () => {
  const [machines, setMachines] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [isEdit, setIsEdit] = useState(false);
  const [pageError, setPageError] = useState("");

  /* =========================================================
     VALIDATION
     ========================================================= */

  const validationSchema = Yup.object({
    departmentId: Yup.string().required("Department is required."),

    machineCode: Yup.string()
      .trim()
      .required("Machine code is required.")
      .max(50, "Machine code cannot exceed 50 characters."),

    machineType: Yup.string()
      .trim()
      .required("Machine name is required.")
      .max(150, "Machine name cannot exceed 150 characters."),

    description: Yup.string().max(
      500,
      "Description cannot exceed 500 characters.",
    ),

    isActive: Yup.boolean(),
  });

  /* =========================================================
     GET MACHINES
     Calls AddOrEditMachine with Action = GET.
     ========================================================= */

  const getMachines = useCallback(async () => {
    try {
      setLoading(true);
      setPageError("");

      const response = await AddOrEditMachine({
        action: "GET",
      });

      const rows = getResponseRows(response).map(normalizeMachine);

      setMachines(rows);
    } catch (error) {
      console.error("Error loading machines:", error);

      setPageError(getErrorMessage(error, "Unable to load machines."));
    } finally {
      setLoading(false);
    }
  }, []);

  /* =========================================================
   GET MACHINE TYPES
   ========================================================= */

  const getMachineTypes = useCallback(async () => {
    try {
      const response = await GetMachineTypes();
      const rows = getResponseRows(response);

      setMachineTypes(
        rows
          .map((item) => ({
            machineType: item.machineType ?? item.MachineType ?? "",
          }))
          .filter((item) => item.machineType),
      );
    } catch (error) {
      console.error("Error loading machine types:", error);
      toast.error("Unable to load machine types.");
    }
  }, []);

  /* =========================================================
     GET DEPARTMENTS
     ========================================================= */

  const getDepartments = useCallback(async () => {
    try {
      setDepartmentsLoading(true);

      const response = await GetMachineDepartments();

      const rows = getResponseRows(response).map((item) => ({
        departmentId: item.departmentId ?? item.DepartmentId ?? "",
        departmentName: item.departmentName ?? item.DepartmentName ?? "",
      }));

      setDepartments(rows);
    } catch (error) {
      console.error("Error loading departments:", error);

      toast.error(getErrorMessage(error, "Unable to load departments."));
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    getMachines();
    getMachineTypes();
    getDepartments();
  }, [getMachines, getMachineTypes, getDepartments]);

  /* =========================================================
     FORMIK
     ========================================================= */

  const formik = useFormik({
    initialValues: emptyForm,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,

    onSubmit: async (values, helpers) => {
      try {
        setSaving(true);

        const payload = {
          action: isEdit ? "UPDATE" : "INSERT",
          id: isEdit ? Number(values.id) : null,
          departmentId: Number(values.departmentId),
          machineCode: values.machineCode.trim(),
          machineName: values.machineName.trim(),
          description: values.description.trim(),

          // New machines are always active.
          // Editing preserves the existing status.
          isActive: isEdit ? values.isActive : true,
        };

        console.log("Machine Payload:", payload);

        const response = await AddOrEditMachine(payload);
        const result = getResult(response);

        console.log("Machine Response:", response);

        if (Number(result?.status ?? result?.Status) === 0) {
          toast.warning(getResultMessage(result) || "Unable to save machine.");
          return;
        }

        toast.success(
          getResultMessage(result) ||
            (isEdit
              ? "Machine updated successfully."
              : "Machine added successfully."),
        );

        helpers.resetForm({ values: emptyForm });
        helpers.setTouched({});
        setIsEdit(false);

        await getMachines();
      } catch (error) {
        console.error("Error saving machine:", error);

        toast.error(getErrorMessage(error, "Unable to save machine."));
      } finally {
        setSaving(false);
        helpers.setSubmitting(false);
      }
    },
  });

  /* =========================================================
     RESET FORM
     ========================================================= */

  const resetForm = () => {
    formik.resetForm({
      values: emptyForm,
    });

    formik.setTouched({});
    setIsEdit(false);
  };

  /* =========================================================
     EDIT MACHINE
     ========================================================= */

  const handleEdit = (item) => {
    const editData = {
      id: item.id,
      departmentId: String(item.departmentId ?? ""),
      machineCode: item.machineCode || "",
      machineName: item.machineName || "",
      description: item.description || "",
      isActive: item.isActive,
    };

    formik.setValues(editData);
    formik.setTouched({});
    setIsEdit(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =========================================================
     CHANGE MACHINE STATUS
     ========================================================= */

  const handleStatusChange = async (item, nextStatus) => {
    try {
      const payload = {
        action: "UPDATE",
        id: item.id,
        isActive: nextStatus,
      };

      await AddOrEditMachine(payload);

      toast.success(
        nextStatus
          ? "Machine activated successfully."
          : "Machine deactivated successfully.",
      );

      await getMachines();
    } catch (error) {
      console.error("Error updating machine status:", error);

      toast.error(getErrorMessage(error, "Unable to update machine status."));
    }
  };

  /* =========================================================
     SEARCH AND STATUS FILTER
     ========================================================= */

  const filteredData = machines.filter((item) => {
    const searchText = search.toLowerCase().trim();

    const matchesSearch =
      !searchText ||
      item.departmentName.toLowerCase().includes(searchText) ||
      item.machineCode.toLowerCase().includes(searchText) ||
      item.machineName.toLowerCase().includes(searchText) ||
      item.description.toLowerCase().includes(searchText);

    const matchesStatus =
      statusFilter === "All" ||
      (statusFilter === "Active" && item.isActive) ||
      (statusFilter === "Inactive" && !item.isActive);

    return matchesSearch && matchesStatus;
  });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <PageContainer
      title="Machine Master"
      description="Create and manage machines"
    >
      <Breadcrumb title="Machine Master" items={BCrumb} />

      {/* ADD / EDIT MACHINE FORM */}

      <Card sx={{ p: 0, mb: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          p={2}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="cpu" width="18" />
          <Box sx={{ ml: 1 }}>Machine Master</Box>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#111827",
              mb: 2,
            }}
          >
            {isEdit ? "Edit Machine" : "Add Machine"}
          </Typography>

          {pageError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setPageError("")}
            >
              {pageError}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit} noValidate>
            <Grid container spacing={2}>
              {/* Department */}
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={departments}
                  loading={departmentsLoading}
                  value={
                    departments.find(
                      (department) =>
                        String(department.departmentId) ===
                        String(formik.values.departmentId),
                    ) ?? null
                  }
                  getOptionLabel={(option) => option.departmentName || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option.departmentId) === String(value.departmentId)
                  }
                  onChange={(_, selectedDepartment) => {
                    formik.setFieldValue(
                      "departmentId",
                      selectedDepartment
                        ? String(selectedDepartment.departmentId)
                        : "",
                    );
                  }}
                  onBlur={() => {
                    formik.setFieldTouched("departmentId", true);
                  }}
                  disabled={departmentsLoading}
                  noOptionsText={
                    departmentsLoading
                      ? "Loading departments..."
                      : "No departments found"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Department"
                      required
                      size="small"
                      placeholder="Search department..."
                      error={
                        formik.touched.departmentId &&
                        Boolean(formik.errors.departmentId)
                      }
                      helperText={
                        formik.touched.departmentId
                          ? formik.errors.departmentId
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {departmentsLoading && (
                              <CircularProgress color="inherit" size={16} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Machine Code */}

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Machine Code"
                  name="machineCode"
                  value={formik.values.machineCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.machineCode &&
                    Boolean(formik.errors.machineCode)
                  }
                  helperText={
                    formik.touched.machineCode ? formik.errors.machineCode : ""
                  }
                  placeholder="e.g. MC-001"
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>

              {/* Machine Name */}

              {/* Machine Type */}
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={machineTypes}
                  loading={machineTypes.length === 0}
                  value={
                    machineTypes.find(
                      (item) => item.machineType === formik.values.machineType,
                    ) ?? null
                  }
                  getOptionLabel={(option) => option.machineType || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.machineType === value.machineType
                  }
                  onChange={(_, selectedType) => {
                    formik.setFieldValue(
                      "machineType",
                      selectedType?.machineType ?? "",
                    );
                  }}
                  onBlur={() => {
                    formik.setFieldTouched("machineType", true);
                  }}
                  noOptionsText="No machine types found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      size="small"
                      label="Machine Type"
                      placeholder="Search machine type..."
                      error={
                        formik.touched.machineType &&
                        Boolean(formik.errors.machineType)
                      }
                      helperText={
                        formik.touched.machineType
                          ? formik.errors.machineType
                          : ""
                      }
                    />
                  )}
                />
              </Grid>

              {/* Description */}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={6}
                  label="Description"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description ? formik.errors.description : ""
                  }
                  placeholder="Enter machine description"
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>

              {/* Active status is intentionally not editable in the form. */}

              {/* Form Buttons */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={saving || departmentsLoading}
                  >
                    {saving ? "Saving..." : isEdit ? "Update" : "Add"}
                  </Button>

                  <Button
                    variant="outlined"
                    type="button"
                    onClick={resetForm}
                    disabled={saving}
                  >
                    {isEdit ? "Cancel" : "Reset"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* MACHINE LIST */}

      <Card sx={{ p: 0 }}>
        <Box
          display="flex"
          alignItems="center"
          p={2}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="list" width="18" />
          <Box sx={{ ml: 1 }}>Machine List</Box>
        </Box>

        <CardContent>
          {/* Search and Status Filter */}

          <Grid container spacing={2} mb={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search department, code, machine name..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <Search
                      sx={{
                        mr: 1,
                        color: "text.secondary",
                      }}
                    />
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="machine-status-label">Status</InputLabel>

                <Select
                  labelId="machine-status-label"
                  value={statusFilter}
                  label="Status"
                  onChange={(event) => {
                    setStatusFilter(event.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "flex-start",
                  md: "flex-end",
                },
              }}
            >
              <Button
                variant="outlined"
                onClick={getMachines}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>

          {/* Machine Table */}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>No.</b>
                  </TableCell>

                  <TableCell>
                    <b>Department</b>
                  </TableCell>

                  <TableCell>
                    <b>Machine Code</b>
                  </TableCell>

                  <TableCell>
                    <b>Machine Name</b>
                  </TableCell>

                  <TableCell>
                    <b>Description</b>
                  </TableCell>

                  {/* Status and Edit share one Action column. */}
                  <TableCell align="center">
                    <b>Action</b>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No machines found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>

                      <TableCell>{item.departmentName || "-"}</TableCell>

                      <TableCell>
                        <Typography fontWeight={600}>
                          {item.machineCode}
                        </Typography>
                      </TableCell>

                      <TableCell>{item.machineName}</TableCell>

                      <TableCell
                        sx={{
                          maxWidth: 300,
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {item.description || "-"}
                      </TableCell>

                      {/* ACTION: STATUS + EDIT */}
                      <TableCell align="center">
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          gap={1}
                        >
                          <Tooltip
                            title={item.isActive ? "Active" : "Inactive"}
                          >
                            <Switch
                              checked={item.isActive}
                              size="small"
                              onChange={(event) =>
                                handleStatusChange(item, event.target.checked)
                              }
                            />
                          </Tooltip>

                          <Tooltip title="Edit">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleEdit(item)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}

          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default MachineMaster;
